import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ProfileModuleType,
  type ProfileModuleDto,
  type ProfileTemplateFieldMode,
} from '@up2stay/up2promo-api-client';
import type { IconList, IconName } from '@up2stay/up2promo-ui';
import {
  Button,
  ConfirmDialog,
  getSocialCustomIconPickerList,
  Icon,
  IconPicker,
  iconPickerI18nDe,
  InlineEditCard,
  Input,
  Label,
  SocialLinkIcon,
} from '@up2stay/up2promo-ui';
import { cn } from '@up2stay/up2promo-ui';
import { ExternalLink, GripVertical, Plus, Trash2 } from 'lucide-react';
import { createElement, useEffect, useMemo, useRef, useState } from 'react';
import type { ModuleEntryGovernanceProps } from '@/core/components/profile/module-entry-governance';
import {
  ModuleEntryListRowBody,
  ModuleEntryOverrideHint,
  ModuleEntryPolicySlot,
} from '@/core/components/profile/module-entry-governance-ui';
import { ProfileFieldLockedHint } from '@/core/components/profile-templates/profile-field-locked-hint';
import { isIndividualFieldMode } from '@/core/components/profile-templates/template-member-value-inputs';
import { useDndProfileSensors } from '@/core/hooks/use-dnd-profile-sensors';
import { useIsMobile } from '@/core/hooks/use-mobile';
import {
  lockBodyScrollDuringDrag,
  triggerDragEndHaptic,
  triggerDragOverHaptic,
  triggerDragStartHaptic,
  unlockBodyScrollAfterDrag,
} from '@/core/lib/dnd-haptics';
import {
  defaultPolicyForNewEntry,
  isEntryPolicyLocked,
  resolveEntryPolicy,
} from '@/core/lib/profile-module-entry-policies';
import { openExternalUrl } from '@/core/lib/open-external-url';
import {
  getSocialHandlePrefix,
  getSocialIcon,
  SOCIAL_ICONS,
  getSocialUrl,
  getSocialUsername,
  parseCustomSocialIconName,
} from '@/core/lib/socials';
import {
  SocialPlatform,
  type SocialLink,
  type SocialsModuleConfig,
} from '@/core/types/profile-modules';
import { SocialEditForm } from '@/core/components/profile/modules/socials-edit-form';
import { SocialPlatformIcon } from '@/core/components/profile/modules/socials-platform-icon';
import { SOCIAL_PLATFORMS } from '@/core/components/profile/modules/socials-shared';

export function socialSortableId(link: SocialLink, index: number): string {
  return `social-${link.id ?? String(index)}`;
}

interface SortableSocialItemProps extends Omit<
  ModuleEntryGovernanceProps,
  'onEntryPolicyChange'
> {
  link: SocialLink;
  index: number;
  sortableId: string;
  onEdit: () => void;
  onRemove: () => void;
  fieldLockedHintMessage?: string;
  entryLocked?: boolean;
  showEntryPolicy?: boolean;
  entryPolicy?: ProfileTemplateFieldMode;
  onEntryPolicyChange?: (mode: ProfileTemplateFieldMode) => void;
  currentModule?: ProfileModuleDto;
  entryId?: string;
  onResetEntry?: () => void;
}

export function SortableSocialEditSlot({
  sortableId,
  children,
}: {
  sortableId: string;
  children: React.ReactNode;
}) {
  const { setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId,
    disabled: true,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? transition : 'none',
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children}
    </div>
  );
}

export function SortableSocialItem({
  link,
  index: _index,
  sortableId,
  onEdit,
  onRemove,
  structureReadOnly = false,
  entryLocked = false,
  showEntryPolicy = false,
  entryPolicy,
  onEntryPolicyChange,
  policyLabels,
  fieldLockedHintMessage,
  showEntryOverrideHints = false,
  currentModule,
  templateModule,
  entryId,
  overrideHintMessage,
  templateOverrideLabels,
  onResetEntry,
  contentReadOnly = false,
  workspaceId,
  templateId,
  publishStatus,

  memberRoster,
  memberRosterLoading,
  hasAssignedTeams,
  memberValuesDict,
}: SortableSocialItemProps) {
  const entryLockedResolved =
    entryLocked ??
    (templateModule && entryId
      ? isEntryPolicyLocked(templateModule, entryId)
      : false);
  const isMobile = useIsMobile();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId, disabled: structureReadOnly });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? transition : 'none',
  };

  const username = getSocialUsername(link.platform, link.url);
  const platformLabel =
    SOCIAL_PLATFORMS.find((p) => p.value === link.platform)?.label ??
    link.platform;
  const customLabel = link.label?.trim();
  const isIndividualEntry =
    showEntryPolicy && isIndividualFieldMode(entryPolicy);
  const primary = customLabel || platformLabel;
  const secondary = isIndividualEntry ? null : username || null;

  const policySlot =
    showEntryPolicy && policyLabels && onEntryPolicyChange ? (
      <ModuleEntryPolicySlot
        showEntryPolicy={showEntryPolicy}
        entryPolicy={entryPolicy}
        onEntryPolicyChange={onEntryPolicyChange}
        policyLabels={policyLabels}
        memberValueColumn={{
          kind: 'social',
          platform: String(link.platform).toLowerCase(),
          label: platformLabel,
        }}
        memberDialogTitle={platformLabel}
        memberDefaultValue={link.url ?? ''}
        memberInputPlaceholder={
          link.platform === 'custom'
            ? 'https://...'
            : 'z.B. deinname oder @deinname'
        }
        workspaceId={workspaceId}
        templateId={templateId}
        publishStatus={publishStatus}
        memberRoster={memberRoster}
        memberRosterLoading={memberRosterLoading}
        hasAssignedTeams={hasAssignedTeams}
        memberValuesDict={memberValuesDict}
        canManage={!contentReadOnly}
      />
    ) : null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'rounded-lg border',
        isDragging &&
          'opacity-80 shadow-lg z-10 bg-background ring-2 ring-primary',
        isMobile && 'touch-pan-y cursor-grab active:cursor-grabbing',
      )}
      {...(isMobile ? { ...listeners, ...attributes } : {})}
    >
      <ModuleEntryListRowBody
        isMobile={isMobile}
        structureReadOnly={structureReadOnly}
        dragHandle={
          <div
            data-dnd-grip
            {...listeners}
            {...attributes}
            className={cn(
              'touch-none shrink-0 rounded p-0.5 cursor-grab active:cursor-grabbing',
            )}
            aria-label="Reihenfolge ändern"
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>
        }
        main={
          <button
            type="button"
            onClick={onEdit}
            disabled={entryLockedResolved && !showEntryPolicy}
            className="flex w-full min-w-0 cursor-pointer items-center gap-3 text-left rounded-md p-2 -m-2 disabled:cursor-default disabled:opacity-70"
          >
            <SocialLinkIcon
              link={link}
              className="h-5 w-5 shrink-0 text-muted-foreground"
            />
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate">{primary}</div>
              {secondary ? (
                <div className="text-sm text-muted-foreground truncate">
                  {secondary}
                </div>
              ) : null}
              {entryLockedResolved && fieldLockedHintMessage ? (
                <ProfileFieldLockedHint
                  locked
                  message={fieldLockedHintMessage}
                  className="mt-1"
                />
              ) : null}
            </div>
          </button>
        }
        policy={policySlot}
        trailing={
          !structureReadOnly ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0 opacity-70 hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              aria-label="Link entfernen"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          ) : null
        }
      />
      {currentModule &&
      templateModule &&
      entryId &&
      onResetEntry &&
      templateOverrideLabels ? (
        <ModuleEntryOverrideHint
          currentModule={currentModule}
          templateModule={templateModule}
          entryId={entryId}
          showEntryOverrideHints={showEntryOverrideHints}
          overrideHintMessage={overrideHintMessage}
          templateOverrideLabels={templateOverrideLabels}
          onReset={onResetEntry}
          className="mt-1.5 px-3 pb-2"
        />
      ) : null}
    </div>
  );
}
